import React from "react";
import styled from "styled-components";

const defaultCss = {
  lineHeight: 1,
  fontSize: 14,
  fontWeight: 400,
  margin: 0,
  wordBreak: "normal",
  textAlign: "center",
};

const DefaultText = styled.span(({ color, theme, size }) => ({
  ...defaultCss,
  color: color || theme.color.text,
  fontSize: size ? size : 14,
}));

const Error = styled(DefaultText)(() => ({
  color: "red",
  fontSize: 12,
}));

const Heading = styled(DefaultText)(({ color, ...props }) => ({
  margin: "0.2rem 0",
  fontWeight: 600,
  color: color || props.theme.color.text,
  fontSize: props.theme.fontSize?.heading,
}));

const Heading1 = styled(Heading)(({ ...props }) => ({
  fontWeight: 700,
  fontSize: props.theme.fontSize?.heading1,
}));

const Heading2 = styled(Heading)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.heading2,
}));

const Heading3 = styled(Heading)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.heading3,
}));

const Heading4 = styled(Heading)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.heading4,
}));

const Text1 = styled(DefaultText)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.text1,
}));

const Text2 = styled(DefaultText)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.text2,
}));

const Text3 = styled(DefaultText)(({ ...props }) => ({
  fontWeight: 600,
  fontSize: props.theme.fontSize?.text3,
}));

const Text4 = styled(DefaultText)(({ ...props }) => ({
  fontSize: props.theme.fontSize?.text4,
}));

const Light = styled(DefaultText)(({ color, size, ...props }) => ({
  fontWeight: 300,
  color: color || props.theme.color.text,
  fontSize: !!size ? size : 14,
}));

const Medium = styled(DefaultText)(({ color, size, ...props }) => ({
  fontWeight: 500,
  color: color || props.theme.color.text,
  fontSize: !!size ? size : 14,
}));

const SemiBold = styled(DefaultText)(({ color, size, ...props }) => ({
  fontWeight: 600,
  color: color || props.theme.color.text,
  fontSize: !!size ? size : 14,
}));

const Bold = styled(DefaultText)(({ color, size, ...props }) => ({
  fontWeight: 700,
  color: color || props.theme.color.text,
  fontSize: !!size ? size : 14,
}));

const TextFactory = ({ variant, theme, color, size, ...props }) => {
  const vars = {
    h0: <Heading color={color} {...props} />,
    h1: <Heading1 color={color} {...props} />,
    h2: <Heading2 color={color} {...props} />,
    h3: <Heading3 color={color} {...props} />,
    h4: <Heading4 color={color} {...props} />,
    t1: <Text1 color={color} {...props} />,
    t2: <Text2 color={color} {...props} />,
    t3: <Text3 color={color} {...props} />,
    t4: <Text4 color={color} {...props} />,
    light: <Light {...props} color={color} size={size} />,
    medium: <Medium color={color} size={size} {...props} />,
    semiBold: <SemiBold color={color} size={size} {...props} />,
    bold: <Bold color={color} size={size} {...props} />,

    error: <Error {...props} />,
  };

  return vars[variant] || <DefaultText color={color} size={size} {...props} />;
};

const Text = ({ v, color, ...props }) => <TextFactory {...props} variant={v} color={color} />;

Text.H0 = ({ v = "h0", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.H1 = ({ v = "h1", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.H2 = ({ v = "h2", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.H3 = ({ v = "h3", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.H4 = ({ v = "h4", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.T1 = ({ v = "t1", color, ...props }) => <TextFactory {...props} variant={v} />;
Text.T2 = ({ v = "t2", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.T3 = ({ v = "t3", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.T4 = ({ v = "t4", color, ...props }) => <TextFactory {...props} variant={v} color={color} />;
Text.Light = ({ v = "light", color, size, ...props }) => (
  <TextFactory {...props} variant={v} color={color} size={size} />
);

Text.Medium = ({ v = "medium", color, size, ...props }) => (
  <TextFactory {...props} variant={v} color={color} size={size} />
);
Text.SemiBold = ({ v = "semiBold", color, size, ...props }) => (
  <TextFactory {...props} variant={v} color={color} size={size} />
);
Text.Bold = ({ v = "bold", color, size, ...props }) => (
  <TextFactory {...props} variant={v} color={color} size={size} />
);

Text.Error = ({ v = "error", color, size, textAlign, ...props }) => (
  <TextFactory {...props} variant={v} color={color} size={size} textAlign={textAlign} />
);

Text.H1.displayName = "Heading1";
Text.H2.displayName = "Heading2";
Text.H3.displayName = "Heading3";
Text.H4.displayName = "Heading4";
Text.T1.displayName = "Text1";
Text.T2.displayName = "Text2";
Text.T3.displayName = "Text3";
Text.T4.displayName = "Text4";
Text.Light.displayName = "Light";
Text.Medium.displayName = "Medium";
Text.SemiBold.displayName = "SemiBold";
Text.Bold.displayName = "Bold";
Text.Error.displayName = "Error";
Text.displayName = "Text";

export default Text;
